import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Stack,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import useStore from '../store/useStore';

// Admin review of AlfaCRM data. AlfaCRM is read-only and our DB is the source
// of truth: backfill only adds attendance, disagreements become pending
// changes the admin accepts or rejects, and hand-made placeholder students can
// be merged with the record the AlfaCRM import created for the same child.
// Props: section/onSectionChange (optional, controlled sub-tab 0..2),
// onDataChanged (called after anything that changes students/attendance).

const HIDDEN_PAIRS_KEY = 'alfaMergeHiddenPairs';

const pad = (n) => String(n).padStart(2, '0');
const toISODate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function lastMonthRange() {
  const now = new Date();
  return {
    from: toISODate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    to: toISODate(new Date(now.getFullYear(), now.getMonth(), 0)),
  };
}

function thisMonthRange() {
  const now = new Date();
  return {
    from: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toISODate(now),
  };
}

// Calendar dates come from the API as 'YYYY-MM-DD' or UTC-midnight ISO strings:
// read the date part as-is so the browser timezone cannot shift the day.
function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

const countOf = (v) => (Array.isArray(v) ? v.length : Number(v) || 0);
const percent = (score) => Math.round((Number(score) || 0) * 100);

const ATTENDANCE_LABELS = {
  present: ['Present', 'Был(а)'],
  absent: ['Absent', 'Не был(а)'],
  late: ['Late', 'Опоздал(а)'],
  excused: ['Excused', 'Уважительная причина'],
};

const CHANGE_TYPE_LABELS = {
  attendance_status: ['Attendance', 'Посещаемость'],
  student_field: ['Student', 'Ученик'],
  parent_field: ['Parent', 'Родитель'],
};

function formatValue(value, type, isEn) {
  if (value === null || value === undefined || value === '') {
    if (type === 'attendance_status') return isEn ? 'No record' : 'Нет отметки';
    return '—';
  }
  if (type === 'attendance_status' && ATTENDANCE_LABELS[value]) {
    return ATTENDANCE_LABELS[value][isEn ? 0 : 1];
  }
  if (typeof value === 'boolean') {
    if (value) return isEn ? 'Yes' : 'Да';
    return isEn ? 'No' : 'Нет';
  }
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value
      .map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)))
      .join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(String(value))) return fmtDate(value);
  return String(value);
}

// GET when body is undefined, otherwise POST JSON. Throws the server message.
async function request(url, body) {
  const opts = { credentials: 'include' };
  if (body !== undefined) {
    opts.method = 'POST';
    opts.headers = { 'Content-Type': 'application/json' };
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(url, opts);
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.message) || `HTTP ${res.status}`);
  return data;
}

function readHiddenPairs() {
  try {
    const list = JSON.parse(sessionStorage.getItem(HIDDEN_PAIRS_KEY));
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function writeHiddenPairs(list) {
  try {
    sessionStorage.setItem(HIDDEN_PAIRS_KEY, JSON.stringify(list));
  } catch (e) {
    // storage unavailable: hiding just won't survive a reload
  }
}

const pairKey = (pair) => `${pair.local._id}:${pair.alfa._id}`;

function Stat({ label, value, color }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2, flex: '1 1 130px', minWidth: 130 }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: color || 'text.primary' }}
      >
        {value || 0}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

// ── 1. Attendance backfill for a date range ──────────────────────────────
function BackfillSection({ isEn, notify, onOpenChanges, onAfterBackfill }) {
  const [preset, setPreset] = useState('last');
  const [range, setRange] = useState(lastMonthRange);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [createMissing, setCreateMissing] = useState(false);
  const [lastRun, setLastRun] = useState(null); // { from, to, createMissing }
  const [students, setStudents] = useState([]);
  const [linked, setLinked] = useState({}); // alfaId -> linked student name
  const [picked, setPicked] = useState({}); // alfaId -> student chosen manually
  const [linkingId, setLinkingId] = useState(null);

  const loadStudents = useCallback(() => {
    request('/api/students')
      .then((list) =>
        setStudents(
          Array.isArray(list)
            ? [...list].sort((a, b) =>
                (a.name || '').localeCompare(b.name || '', 'ru')
              )
            : []
        )
      )
      .catch(() => setStudents([]));
  }, []);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const applyPreset = (value) => {
    setPreset(value);
    setRange(value === 'this' ? thisMonthRange() : lastMonthRange());
  };

  const setDate = (key, value) => {
    setPreset('custom');
    setRange((prev) => ({ ...prev, [key]: value }));
  };

  const rangeValid = Boolean(range.from && range.to && range.from <= range.to);

  const runBackfill = async (run) => {
    setLoading(true);
    setError(null);
    try {
      const data = await request('/api/admin/alfacrm/backfill', {
        from: run.from,
        to: run.to,
        createMissing: run.createMissing,
      });
      setResult(data);
      setLastRun(run);
      setLinked({});
      setPicked({});
      // new students must show up in the manual-link picker
      if (data && data.createdStudents > 0) loadStudents();
      onAfterBackfill();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const link = async (customer, student) => {
    if (!student || !student.id) return;
    setLinkingId(customer.alfaId);
    try {
      await request(`/api/admin/students/${student.id}/link-alfa`, {
        alfaId: customer.alfaId,
      });
      setLinked((prev) => ({ ...prev, [customer.alfaId]: student.name }));
      notify(
        isEn
          ? `AlfaCRM “${customer.name}” linked to ${student.name}`
          : `AlfaCRM «${customer.name}» связан(а) с учеником ${student.name}`
      );
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setLinkingId(null);
    }
  };

  const unmatched = (result && result.unmatchedCustomers) || [];
  const linkedCount = Object.keys(linked).length;
  const conflicts = (result && result.conflicts) || 0;
  const created = (result && result.createdStudents) || 0;
  const ranWithCreate = Boolean(lastRun && lastRun.createMissing);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isEn
          ? 'Reads only attendance for the chosen period. AlfaCRM is not changed; marks already in the app are not overwritten — differences go to “Differences”.'
          : 'Читается только посещаемость за выбранный период. AlfaCRM не изменяется; отметки, которые уже есть в приложении, не перезаписываются — несовпадения попадут в «Расхождения».'}
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 1 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={preset}
          onChange={(e, value) => value && applyPreset(value)}
        >
          <ToggleButton value="last">
            {isEn ? 'Last month' : 'Прошлый месяц'}
          </ToggleButton>
          <ToggleButton value="this">
            {isEn ? 'This month' : 'Этот месяц'}
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          type="date"
          size="small"
          label={isEn ? 'From' : 'С'}
          InputLabelProps={{ shrink: true }}
          value={range.from}
          onChange={(e) => setDate('from', e.target.value)}
        />
        <TextField
          type="date"
          size="small"
          label={isEn ? 'To' : 'По'}
          InputLabelProps={{ shrink: true }}
          value={range.to}
          onChange={(e) => setDate('to', e.target.value)}
          error={Boolean(range.from && range.to && range.from > range.to)}
        />
        <Button
          variant="contained"
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <CloudDownloadIcon />
            )
          }
          disabled={loading || !rangeValid}
          onClick={() => runBackfill({ ...range, createMissing })}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {isEn ? 'Pull from AlfaCRM' : 'Подтянуть из AlfaCRM'}
        </Button>
      </Stack>
      <FormControlLabel
        sx={{ mb: 2 }}
        control={
          <Checkbox
            checked={createMissing}
            onChange={(e) => setCreateMissing(e.target.checked)}
            disabled={loading}
          />
        }
        label={
          isEn
            ? 'Create new students from AlfaCRM (if they are not in the app)'
            : 'Создать новых учеников из AlfaCRM (если их нет в приложении)'
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography sx={{ fontWeight: 700 }}>
            {isEn
              ? 'Reading lessons from AlfaCRM…'
              : 'Читаем занятия из AlfaCRM…'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEn
              ? 'This can take up to a minute.'
              : 'Это может занять до минуты.'}
          </Typography>
        </Box>
      )}

      {!loading && result && (
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 700, display: 'block', mb: 1 }}
          >
            {isEn ? 'Period' : 'Период'}{' '}
            {fmtDate(result.from || (lastRun && lastRun.from))} —{' '}
            {fmtDate(result.to || (lastRun && lastRun.to))}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Stat
              label={isEn ? 'Lessons read' : 'Занятий прочитано'}
              value={result.lessons}
            />
            <Stat
              label={isEn ? 'Marks added' : 'Отметок добавлено'}
              value={result.added}
              color="success.main"
            />
            <Stat
              label={isEn ? 'Already present' : 'Уже были'}
              value={result.unchanged}
            />
            <Stat
              label={isEn ? 'Conflicts' : 'Расхождений'}
              value={conflicts}
              color={conflicts > 0 ? 'warning.main' : undefined}
            />
            <Stat
              label={isEn ? 'Students matched' : 'Учеников сопоставлено'}
              value={result.matchedStudents}
            />
            {(ranWithCreate || created > 0) && (
              <Stat
                label={isEn ? 'Students created' : 'Создано учеников'}
                value={created}
                color={created > 0 ? 'success.main' : undefined}
              />
            )}
          </Box>

          {conflicts > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 2, alignItems: 'center' }}
              action={
                <Button
                  color="warning"
                  size="small"
                  variant="outlined"
                  onClick={onOpenChanges}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {isEn ? 'Review' : 'Посмотреть'}
                </Button>
              }
            >
              {isEn
                ? `Differences with AlfaCRM: ${conflicts}. The app values were kept — decide in “Differences”.`
                : `Расхождений с AlfaCRM: ${conflicts}. Значения приложения сохранены — решите во вкладке «Расхождения».`}
            </Alert>
          )}

          {linkedCount > 0 && (
            <Alert
              severity="info"
              sx={{ mb: 2, alignItems: 'center' }}
              action={
                <Button
                  color="info"
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<RefreshIcon />}
                  onClick={() =>
                    runBackfill(lastRun || { ...range, createMissing })
                  }
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {isEn ? 'Pull again' : 'Подтянуть ещё раз'}
                </Button>
              }
            >
              {isEn
                ? `Linked: ${linkedCount}. Pull the same period again to bring in their lessons.`
                : `Связано: ${linkedCount}. Подтяните тот же период ещё раз, чтобы перенести их занятия.`}
            </Alert>
          )}

          {unmatched.length === 0 ? (
            <Alert severity="success">
              {isEn
                ? 'Every AlfaCRM customer in these lessons is matched to a student.'
                : 'Все клиенты AlfaCRM из этих занятий сопоставлены с учениками.'}
            </Alert>
          ) : (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 3 }}>
                {`${isEn ? 'Not found in the app' : 'Не найдены в приложении'} (${unmatched.length})`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {isEn
                  ? 'These AlfaCRM customers had lessons in the period but are not linked to any student, so their attendance was not imported.'
                  : 'У этих клиентов AlfaCRM были занятия за период, но они не связаны ни с одним учеником приложения, поэтому их посещаемость не перенесена.'}
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                {ranWithCreate
                  ? isEn
                    ? 'The app already has a student with the same name for each of these, so no new student was created. Link them to the existing student, then pull again.'
                    : 'Для каждого из них в приложении уже есть ученик с таким же именем, поэтому новые не созданы. Свяжите их с существующим учеником и подтяните период ещё раз.'
                  : isEn
                    ? 'These are new AlfaCRM students who are not in the app yet. Link each to an existing student, or run again with “Create new students” ticked.'
                    : 'Это новые ученики AlfaCRM, которых нет в приложении. Свяжите с существующим учеником или запустите ещё раз с галочкой «Создать новых учеников».'}
              </Alert>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {isEn ? 'AlfaCRM customer' : 'Клиент AlfaCRM'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {isEn ? 'Lessons' : 'Занятий'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {isEn ? 'Link to a student' : 'Связать с учеником'}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unmatched.map((customer) => {
                      const busy = linkingId === customer.alfaId;
                      const linkedName = linked[customer.alfaId];
                      const manual = picked[customer.alfaId] || null;
                      return (
                        <TableRow key={customer.alfaId} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {customer.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              id {customer.alfaId}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {customer.lessons}
                          </TableCell>
                          <TableCell>
                            {linkedName ? (
                              <Chip
                                color="success"
                                icon={<LinkIcon />}
                                label={`${isEn ? 'Linked to' : 'Связан(а) с'}: ${linkedName}`}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                  alignItems: 'center',
                                }}
                              >
                                {(customer.suggestions || []).map((s) => (
                                  <Chip
                                    key={s.studentId}
                                    clickable
                                    variant="outlined"
                                    color="primary"
                                    icon={<LinkIcon />}
                                    disabled={busy}
                                    label={`${isEn ? 'Link to' : 'Связать с'}: ${s.name} (${percent(s.score)}%)`}
                                    onClick={() =>
                                      link(customer, {
                                        id: s.studentId,
                                        name: s.name,
                                      })
                                    }
                                  />
                                ))}
                                <Autocomplete
                                  size="small"
                                  options={students}
                                  value={manual}
                                  onChange={(e, value) =>
                                    setPicked((prev) => ({
                                      ...prev,
                                      [customer.alfaId]: value,
                                    }))
                                  }
                                  getOptionLabel={(s) => s.name || ''}
                                  isOptionEqualToValue={(a, b) =>
                                    a._id === b._id
                                  }
                                  renderOption={(props, s) => {
                                    // own key: two students may share a name
                                    const { key, ...rest } = props;
                                    return (
                                      <li key={s._id} {...rest}>
                                        {s.name}
                                      </li>
                                    );
                                  }}
                                  sx={{ width: 220 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={
                                        isEn
                                          ? 'Other student…'
                                          : 'Другой ученик…'
                                      }
                                    />
                                  )}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!manual || busy}
                                  startIcon={
                                    busy ? (
                                      <CircularProgress
                                        size={14}
                                        color="inherit"
                                      />
                                    ) : (
                                      <LinkIcon />
                                    )
                                  }
                                  onClick={() =>
                                    link(customer, {
                                      id: manual._id,
                                      name: manual.name,
                                    })
                                  }
                                >
                                  {isEn ? 'Link' : 'Связать'}
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── 2. Pending AlfaCRM ↔ app differences ─────────────────────────────────
function ChangesSection({ isEn, notify, refreshKey, onDecided }) {
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    request(`/api/admin/sync-changes?status=${status}`)
      .then((list) => {
        if (cancelled) return;
        setItems(Array.isArray(list) ? list : []);
        setSelected(new Set());
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, refreshKey, tick]);

  const isPending = status === 'pending';
  const q = search.trim().toLowerCase();
  const visible = q
    ? items.filter(
        (c) =>
          (c.studentName || '').toLowerCase().includes(q) ||
          (c.label || c.field || '').toLowerCase().includes(q)
      )
    : items;
  const selectedVisible = visible.filter((c) => selected.has(c._id));
  const allSelected =
    visible.length > 0 && selectedVisible.length === visible.length;

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(visible.map((c) => c._id)));

  const decideOne = async (change, action) => {
    setBusy(true);
    try {
      const doc = await request(
        `/api/admin/sync-changes/${change._id}/${action}`,
        {}
      );
      setItems((prev) =>
        isPending
          ? prev.filter((c) => c._id !== change._id)
          : prev.map((c) => (c._id === change._id ? doc || c : c))
      );
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(change._id);
        return next;
      });
      onDecided(action === 'accept');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const decideBulk = async (action) => {
    const ids = selectedVisible.map((c) => c._id);
    if (!ids.length) return;
    if (
      action === 'accept' &&
      !window.confirm(
        isEn
          ? `Accept AlfaCRM values for the selected differences (${ids.length})? The app data will be replaced.`
          : `Принять значения AlfaCRM для выбранных расхождений (${ids.length})? Данные приложения будут заменены.`
      )
    )
      return;
    setBusy(true);
    try {
      const data = await request('/api/admin/sync-changes/bulk', {
        ids,
        action,
      });
      const n = (data && data.updated) || 0;
      notify(
        action === 'accept'
          ? isEn
            ? `Accepted from AlfaCRM: ${n}`
            : `Принято из AlfaCRM: ${n}`
          : isEn
            ? `Kept app values: ${n}`
            : `Оставлено наше: ${n}`
      );
      onDecided(action === 'accept');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
      setTick((t) => t + 1);
    }
  };

  const statusChip = (s) => {
    if (s === 'accepted')
      return (
        <Chip
          size="small"
          color="warning"
          label={isEn ? 'AlfaCRM accepted' : 'Принято AlfaCRM'}
        />
      );
    if (s === 'rejected')
      return (
        <Chip
          size="small"
          color="success"
          label={isEn ? 'App kept' : 'Оставлено наше'}
        />
      );
    return <Chip size="small" label={isEn ? 'Pending' : 'Ожидает'} />;
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={status}
          onChange={(e, value) => value && setStatus(value)}
        >
          <ToggleButton value="pending">
            {isEn ? 'Pending' : 'Ожидают'}
          </ToggleButton>
          <ToggleButton value="accepted">
            {isEn ? 'Accepted' : 'Принятые'}
          </ToggleButton>
          <ToggleButton value="rejected">
            {isEn ? 'Rejected' : 'Отклонённые'}
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder={isEn ? 'Student or field' : 'Ученик или поле'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220 }}
        />
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => setTick((t) => t + 1)}
          disabled={loading}
        >
          {isEn ? 'Refresh' : 'Обновить'}
        </Button>
      </Box>

      {isPending && selectedVisible.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, mr: 1 }}>
            {isEn ? 'Selected' : 'Выбрано'}: {selectedVisible.length}
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="success"
            disableElevation
            disabled={busy}
            onClick={() => decideBulk('reject')}
          >
            {isEn ? 'Keep ours' : 'Оставить наше'} ({selectedVisible.length})
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={busy}
            onClick={() => decideBulk('accept')}
          >
            {`${isEn ? 'Accept AlfaCRM' : 'Принять AlfaCRM'} (${selectedVisible.length})`}
          </Button>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : visible.length === 0 ? (
        !error && (
          <Alert severity={isPending ? 'success' : 'info'}>
            {isPending
              ? isEn
                ? 'No differences: the app matches AlfaCRM.'
                : 'Расхождений нет: данные приложения совпадают с AlfaCRM.'
              : isEn
                ? 'Nothing here yet.'
                : 'Здесь пока пусто.'}
          </Alert>
        )
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2, maxHeight: 600 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {isPending && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selectedVisible.length > 0 && !allSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700 }}>
                  {isEn ? 'Student' : 'Ученик'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {isEn ? 'What' : 'Что'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {isEn ? 'Date' : 'Дата'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {isEn ? 'In the app' : 'В приложении'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {isEn ? 'In AlfaCRM' : 'В AlfaCRM'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  {isPending
                    ? isEn
                      ? 'Decision'
                      : 'Решение'
                    : isEn
                      ? 'Status'
                      : 'Статус'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((change) => {
                const typeLabel = CHANGE_TYPE_LABELS[change.type];
                return (
                  <TableRow
                    key={change._id}
                    hover
                    selected={selected.has(change._id)}
                  >
                    {isPending && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(change._id)}
                          onChange={() => toggle(change._id)}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>
                      {change.studentName || '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {change.label || change.field}
                      </Typography>
                      {typeLabel && (
                        <Typography variant="caption" color="text.secondary">
                          {typeLabel[isEn ? 0 : 1]}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {fmtDate(change.date)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, wordBreak: 'break-word' }}>
                      {formatValue(change.localValue, change.type, isEn)}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 220,
                        wordBreak: 'break-word',
                        fontWeight: 600,
                      }}
                    >
                      {formatValue(change.alfaValue, change.type, isEn)}
                    </TableCell>
                    <TableCell align="right">
                      {change.status === 'pending' ? (
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'flex-end',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={busy}
                            onClick={() => decideOne(change, 'accept')}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {isEn ? 'Accept AlfaCRM' : 'Принять AlfaCRM'}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disableElevation
                            disabled={busy}
                            onClick={() => decideOne(change, 'reject')}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {isEn ? 'Keep ours' : 'Оставить наше'}
                          </Button>
                        </Box>
                      ) : (
                        statusChip(change.status)
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ── 3. Merge hand-made placeholders with AlfaCRM-imported records ─────────
function StudentCard({ student, kind, isEn }) {
  const isAlfa = kind === 'alfa';
  const groups = Array.isArray(student.groups) ? student.groups : [];
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        borderColor: isAlfa ? 'info.light' : 'divider',
      }}
    >
      <Chip
        size="small"
        variant="outlined"
        color={isAlfa ? 'info' : 'default'}
        label={
          isAlfa
            ? `${isEn ? 'From AlfaCRM' : 'Из AlfaCRM'}${student.alfaId ? ` · id ${student.alfaId}` : ''}`
            : isEn
              ? 'Created in the app'
              : 'Создан в приложении'
        }
        sx={{ mb: 1 }}
      />
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{student.name}</Typography>
      <Typography variant="body2">
        {isEn ? 'Assessments' : 'Оценок'}:{' '}
        <strong>{countOf(student.profiles)}</strong>
      </Typography>
      <Typography variant="body2">
        {isEn ? 'Attendance marks' : 'Посещений'}:{' '}
        <strong>{countOf(student.attendance)}</strong>
      </Typography>
      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {groups.length ? (
          groups.map((g, i) => (
            <Chip key={`${g}-${i}`} size="small" label={g} />
          ))
        ) : (
          <Typography variant="caption" color="text.secondary">
            {isEn ? 'No group' : 'Без группы'}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function MergeSection({ isEn, notify, onMerged }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState(readHiddenPairs);
  const [tick, setTick] = useState(0);
  // { pair, intoLocal: target is the local record, keepSide: 'local'|'alfa' }
  const [confirm, setConfirm] = useState(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    request('/api/admin/students/merge-candidates')
      .then((list) => {
        if (cancelled) return;
        setCandidates(
          (Array.isArray(list) ? list : [])
            .filter((c) => c && c.local && c.alfa)
            .sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0))
        );
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const hide = (pair) => {
    const next = [...hidden, pairKey(pair)];
    setHidden(next);
    writeHiddenPairs(next);
  };

  const unhideAll = () => {
    setHidden([]);
    writeHiddenPairs([]);
  };

  const visible = candidates.filter((c) => !hidden.includes(pairKey(c)));
  const hiddenCount = candidates.length - visible.length;

  const target = confirm
    ? confirm.intoLocal
      ? confirm.pair.local
      : confirm.pair.alfa
    : null;
  const source = confirm
    ? confirm.intoLocal
      ? confirm.pair.alfa
      : confirm.pair.local
    : null;

  const doMerge = async () => {
    if (!confirm) return;
    const keptSide = confirm.intoLocal ? 'local' : 'alfa';
    setMerging(true);
    try {
      const data = await request('/api/admin/students/merge', {
        targetId: target._id,
        sourceId: source._id,
        keepName: confirm.keepSide === keptSide ? 'target' : 'source',
      });
      const m = (data && data.moved) || {};
      const name = (data && data.student && data.student.name) || target.name;
      notify(
        isEn
          ? `Merged into “${name}”. Moved: ${countOf(m.profiles)} assessments, ${countOf(m.attendanceAdded)} attendance marks, ${countOf(m.lessonNotes)} notes, ${countOf(m.groups)} groups, ${countOf(m.parents)} parents.`
          : `Объединено в «${name}». Перенесено: оценок ${countOf(m.profiles)}, посещений ${countOf(m.attendanceAdded)}, заметок ${countOf(m.lessonNotes)}, групп ${countOf(m.groups)}, родителей ${countOf(m.parents)}.`
      );
      // Both records changed (one is gone): drop every pair touching them,
      // then reload so the remaining suggestions reflect the merge.
      const touched = [target._id, source._id];
      setCandidates((prev) =>
        prev.filter(
          (c) => !touched.includes(c.local._id) && !touched.includes(c.alfa._id)
        )
      );
      setConfirm(null);
      setTick((t) => t + 1);
      onMerged();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setMerging(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 720 }}
        >
          {isEn
            ? 'Left: a student created by hand in the app (not linked to AlfaCRM). Right: the record the AlfaCRM import created, probably for the same child. Merging moves everything into one student.'
            : 'Слева — ученик, созданный вручную в приложении (без связи с AlfaCRM). Справа — запись, которую создал импорт из AlfaCRM, вероятно для того же ребёнка. Объединение переносит всё в одну карточку.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hiddenCount > 0 && (
            <Button size="small" color="inherit" onClick={unhideAll}>
              {isEn ? 'Show hidden' : 'Показать скрытые'} ({hiddenCount})
            </Button>
          )}
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => setTick((t) => t + 1)}
            disabled={loading}
          >
            {isEn ? 'Refresh' : 'Обновить'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && candidates.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : visible.length === 0 ? (
        !error && (
          <Alert severity="success">
            {isEn
              ? 'No likely duplicates between app students and AlfaCRM records.'
              : 'Похожих пар «ученик приложения — запись AlfaCRM» не найдено.'}
          </Alert>
        )
      ) : (
        <Stack spacing={2}>
          {visible.map((pair) => {
            const score = percent(pair.score);
            return (
              <Paper
                key={pairKey(pair)}
                variant="outlined"
                sx={{ p: 2, borderRadius: 3 }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
                    gap: 2,
                    alignItems: 'stretch',
                  }}
                >
                  <StudentCard student={pair.local} kind="local" isEn={isEn} />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Chip
                      label={`${score}%`}
                      color={
                        score >= 90
                          ? 'success'
                          : score >= 75
                            ? 'warning'
                            : 'default'
                      }
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {isEn ? 'name match' : 'сходство имён'}
                    </Typography>
                  </Box>
                  <StudentCard student={pair.alfa} kind="alfa" isEn={isEn} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mt: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button color="inherit" onClick={() => hide(pair)}>
                    {isEn ? 'Different children' : 'Это разные дети'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CallMergeIcon />}
                    onClick={() =>
                      setConfirm({ pair, intoLocal: true, keepSide: 'local' })
                    }
                  >
                    {isEn ? 'Merge' : 'Объединить'}
                  </Button>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog
        open={Boolean(confirm)}
        onClose={merging ? undefined : () => setConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEn ? 'Merge students' : 'Объединить учеников'}
        </DialogTitle>
        {confirm && (
          <DialogContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {isEn ? 'Which record stays' : 'Какая запись остаётся'}
            </Typography>
            <RadioGroup
              value={confirm.intoLocal ? 'local' : 'alfa'}
              onChange={(e) =>
                setConfirm({
                  ...confirm,
                  intoLocal: e.target.value === 'local',
                })
              }
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="local"
                control={<Radio size="small" />}
                label={
                  isEn
                    ? 'The app record (recommended) — AlfaCRM data moves into it'
                    : 'Запись приложения (рекомендуется) — в неё переносится запись AlfaCRM'
                }
              />
              <FormControlLabel
                value="alfa"
                control={<Radio size="small" />}
                label={
                  isEn
                    ? 'The AlfaCRM record — app data moves into it'
                    : 'Запись AlfaCRM — в неё переносится запись приложения'
                }
              />
            </RadioGroup>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                gap: 1.5,
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                >
                  {isEn ? 'Will be deleted' : 'Будет удалена'}
                </Typography>
                <StudentCard
                  student={source}
                  kind={confirm.intoLocal ? 'alfa' : 'local'}
                  isEn={isEn}
                />
              </Box>
              <ArrowForwardIcon
                color="action"
                sx={{ justifySelf: 'center', mt: { sm: 2.5 } }}
              />
              <Box>
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                >
                  {isEn ? 'Stays' : 'Остаётся'}
                </Typography>
                <StudentCard
                  student={target}
                  kind={confirm.intoLocal ? 'local' : 'alfa'}
                  isEn={isEn}
                />
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {isEn ? 'Name to keep' : 'Какое имя оставить'}
            </Typography>
            <RadioGroup
              value={confirm.keepSide}
              onChange={(e) =>
                setConfirm({ ...confirm, keepSide: e.target.value })
              }
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="local"
                control={<Radio size="small" />}
                label={`${confirm.pair.local.name} (${isEn ? 'app' : 'приложение'})`}
              />
              <FormControlLabel
                value="alfa"
                control={<Radio size="small" />}
                label={`${confirm.pair.alfa.name} (AlfaCRM)`}
              />
            </RadioGroup>

            <Alert severity="warning">
              {isEn
                ? `Move everything from “${source.name}” into “${target.name}” and delete “${source.name}”? Assessments, attendance, notes, groups and parents will be moved. This cannot be undone (a database backup exists).`
                : `Перенести всё из «${source.name}» в «${target.name}» и удалить «${source.name}»? Оценки, посещаемость, заметки, группы и родители будут перенесены. Действие необратимо (есть резервная копия базы).`}
            </Alert>
          </DialogContent>
        )}
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setConfirm(null)}
            disabled={merging}
          >
            {isEn ? 'Cancel' : 'Отмена'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={doMerge}
            disabled={merging}
            startIcon={
              merging ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CallMergeIcon />
              )
            }
          >
            {isEn ? 'Merge' : 'Объединить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function AlfaReviewPanel({
  section,
  onSectionChange,
  onDataChanged,
}) {
  const { language } = useStore();
  const isEn = language === 'en';

  const [ownSection, setOwnSection] = useState(0);
  const current = section === undefined ? ownSection : section;
  // Sub-tabs mount on first visit and then stay mounted (hidden), so a
  // backfill result or a half-done review survives switching tabs.
  const [visited, setVisited] = useState({});
  const [pendingCount, setPendingCount] = useState(null);
  const [changesKey, setChangesKey] = useState(0);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    setVisited((prev) => (prev[current] ? prev : { ...prev, [current]: true }));
  }, [current]);

  const goTo = (value) => {
    setOwnSection(value);
    if (onSectionChange) onSectionChange(value);
  };

  const notify = useCallback(
    (message, severity = 'success') =>
      setSnack({ open: true, message, severity }),
    []
  );

  const refreshPendingCount = useCallback(async () => {
    try {
      const list = await request('/api/admin/sync-changes?status=pending');
      setPendingCount(Array.isArray(list) ? list.length : null);
    } catch (err) {
      setPendingCount(null);
    }
  }, []);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  const handleAfterBackfill = () => {
    setChangesKey((k) => k + 1);
    refreshPendingCount();
    if (onDataChanged) onDataChanged();
  };

  const handleDecided = (accepted) => {
    refreshPendingCount();
    if (accepted && onDataChanged) onDataChanged();
  };

  const isShown = (i) => current === i || visited[i];

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {isEn ? 'AlfaCRM: review & merge' : 'AlfaCRM: проверка и объединение'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isEn
          ? 'AlfaCRM is only read. App data is never overwritten: new data is added, differences wait for an admin decision.'
          : 'AlfaCRM только читается. Данные приложения не перезаписываются: новое добавляется, расхождения попадают на проверку администратору.'}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={current}
          onChange={(e, value) => goTo(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab
            icon={<CloudDownloadIcon />}
            iconPosition="start"
            label={isEn ? 'Pull attendance' : 'Подтянуть посещаемость'}
          />
          <Tab
            icon={<CompareArrowsIcon />}
            iconPosition="start"
            label={`${isEn ? 'Differences' : 'Расхождения'}${pendingCount ? ` (${pendingCount})` : ''}`}
          />
          <Tab
            icon={<CallMergeIcon />}
            iconPosition="start"
            label={isEn ? 'Merge students' : 'Объединить учеников'}
          />
        </Tabs>
      </Box>

      {isShown(0) && (
        <div hidden={current !== 0}>
          <BackfillSection
            isEn={isEn}
            notify={notify}
            onOpenChanges={() => goTo(1)}
            onAfterBackfill={handleAfterBackfill}
          />
        </div>
      )}
      {isShown(1) && (
        <div hidden={current !== 1}>
          <ChangesSection
            isEn={isEn}
            notify={notify}
            refreshKey={changesKey}
            onDecided={handleDecided}
          />
        </div>
      )}
      {isShown(2) && (
        <div hidden={current !== 2}>
          <MergeSection
            isEn={isEn}
            notify={notify}
            onMerged={() => {
              if (onDataChanged) onDataChanged();
            }}
          />
        </div>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
